/**
 * useProtocolAudio
 *
 * Plays pre-recorded MP3s hosted on CDN for each protocol step.
 * Replaces the Web Speech API / ElevenLabs on-the-fly approach.
 *
 * Audio files live at:
 *   ${REACT_APP_AUDIO_BASE_URL}/<filename>.mp3
 *
 * Each step has up to 3 cues:
 *   intro  — plays at step start, timer is frozen until it finishes
 *   during — plays once intro ends, timer running
 *   outro  — plays 15 seconds before step end (or at 90s mark for Trataka)
 *
 * Yoga Nidra (INSTALL) uses a sequential body-scan playlist instead:
 *   nsdr_intro → nsdr_body_1 → nsdr_body_2 → nsdr_body_3 → nsdr_body_4
 *   → nsdr_deeprest → nsdr_outro
 *
 * Fallback: if audio is disabled (muted) or a file 404s, the hook
 * resolves silently and never blocks the timer.
 */

import { useCallback, useRef } from 'react';

const BASE = process.env.REACT_APP_AUDIO_BASE_URL || '';

// Local audio files served from /audio/ in public dir
const LOCAL_AUDIO = {
  yoga_nidra: {
    420:  '/audio/yoga-nidra-7min.mp3',
    600:  '/audio/yoga-nidra-10min.mp3',
    900:  '/audio/yoga-nidra-15min.mp3',
  },
};

// Map step id → file stems (without .mp3) for CDN-hosted files
const AUDIO_MAP = {
  kapalbhati:   { intro: 'kapalbhati_intro',  during: 'kapalbhati_during',  outro: 'kapalbhati_outro'  },
  nadi_shodhana:{ intro: 'nadi_intro',         during: 'nadi_during',         outro: 'nadi_outro'         },
  bhramari:     { intro: 'bhramari_intro',     during: 'bhramari_during',     outro: 'bhramari_outro'     },
  rechaka:      { intro: 'rechaka_intro',      during: 'rechaka_during',      outro: 'rechaka_outro'      },
  trataka:      { intro: 'trataka_intro',      during: 'trataka_during',      outro: 'trataka_outro'      },
  yoga_nidra:   {
    intro:    'nsdr_intro',
    playlist: ['nsdr_body_1', 'nsdr_body_2', 'nsdr_body_3', 'nsdr_body_4', 'nsdr_deeprest'],
    outro:    'nsdr_outro',
  },
};

const SILENCE_GAP_MS = 1000; // gap between NSDR body files

const url = (stem) => `${BASE}/${stem}.mp3`;

export const useProtocolAudio = () => {
  const audioRef    = useRef(null); // current HTMLAudioElement
  const mountedRef  = useRef(true);
  const cancelRef   = useRef(false);

  // ── Internal helpers ─────────────────────────────────────────

  const stopCurrent = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
  }, []);

  /** Play a single file by stem from CDN, resolve when it ends or errors. Never throws. */
  const playSingle = useCallback((stem) => {
    return new Promise((resolve) => {
      if (cancelRef.current || !BASE) { resolve(); return; }

      stopCurrent();
      const audio = new Audio(url(stem));
      audioRef.current = audio;

      const done = () => { audioRef.current = null; resolve(); };

      audio.onended  = done;
      audio.onerror  = () => { console.warn(`[audio] failed: ${stem}`); done(); };
      audio.onabort  = done;

      audio.play().catch(() => done());
    });
  }, [stopCurrent]);

  /** Play a direct URL (e.g. local /audio/ file). Never throws. */
  const playUrl = useCallback((fileUrl) => {
    return new Promise((resolve) => {
      if (cancelRef.current) { resolve(); return; }

      stopCurrent();
      const audio = new Audio(fileUrl);
      audioRef.current = audio;

      const done = () => { audioRef.current = null; resolve(); };

      audio.onended  = done;
      audio.onerror  = () => { console.warn(`[audio] failed: ${fileUrl}`); done(); };
      audio.onabort  = done;

      audio.play().catch(() => done());
    });
  }, [stopCurrent]);

  /** Play an array of stems with a silence gap between each. */
  const playPlaylist = useCallback(async (stems) => {
    for (const stem of stems) {
      if (cancelRef.current) break;
      await playSingle(stem);
      if (!cancelRef.current) {
        await new Promise(r => setTimeout(r, SILENCE_GAP_MS));
      }
    }
  }, [playSingle]);

  // ── Public API ───────────────────────────────────────────────

  /**
   * playIntro(stepId)
   * Plays the intro cue for the given step.
   * Returns a Promise that resolves when the cue finishes.
   * Caller should freeze the timer while this is pending.
   */
  const playIntro = useCallback(async (stepId) => {
    cancelRef.current = false;
    const map = AUDIO_MAP[stepId];
    if (!map?.intro) return;
    await playSingle(map.intro);
  }, [playSingle]);

  /**
   * playDuring(stepId, duration)
   * For most steps: plays the single "during" cue from CDN.
   * For yoga_nidra: plays the local MP3 matching the chosen duration (420/600/900s).
   * Returns a Promise that resolves when audio finishes.
   */
  const playDuring = useCallback(async (stepId, duration) => {
    const map = AUDIO_MAP[stepId];
    if (!map) return;

    if (stepId === 'yoga_nidra') {
      // Play the single local Yoga Nidra file for the selected duration
      const localFile = LOCAL_AUDIO.yoga_nidra?.[duration];
      if (localFile) {
        await playUrl(localFile);
      } else if (map.playlist) {
        await playPlaylist(map.playlist);
      }
    } else {
      if (map.during) await playSingle(map.during);
    }
  }, [playSingle, playPlaylist, playUrl]);

  /**
   * playOutro(stepId)
   * Plays the outro cue. For yoga_nidra this is nsdr_outro.
   */
  const playOutro = useCallback(async (stepId) => {
    const map = AUDIO_MAP[stepId];
    if (!map?.outro) return;
    await playSingle(map.outro);
  }, [playSingle]);

  /**
   * stop()
   * Immediately stops any playing audio and cancels any pending playlist.
   */
  const stop = useCallback(() => {
    cancelRef.current = true;
    stopCurrent();
  }, [stopCurrent]);

  /**
   * isConfigured()
   * Returns true if the CDN base URL env var is set.
   * Use this to decide whether to show audio UI or fall back gracefully.
   */
  const isConfigured = useCallback(() => !!BASE, []);

  return { playIntro, playDuring, playOutro, stop, isConfigured };
};
